import { isRecord, type JsonRecord } from './api-client';
import {
  adminMenuPermissions,
  canonicalBusinessFieldName,
  ensureCommonReadFieldPolicies,
  businessViewDependencies,
  type BusinessReadFieldRule,
} from '@travel/contracts';
import { adminPages } from './page-registry';
import { moduleEntries, modulePathForPage } from './admin-navigation';

export type SecurityTab =
  'staff' | 'roles' | 'menus' | 'scope' | 'fields' | 'sensitive' | 'organizations' | 'access';
export const SECURITY_TABS: { id: SecurityTab; name: string }[] = [
  { id: 'organizations', name: '组织架构' },
  { id: 'roles', name: '角色管理' },
  { id: 'staff', name: '系统用户' },
];
export function tabForSecurityPage(pageId: string): SecurityTab {
  return (
    (
      {
        'ADM-J01': 'staff',
        'ADM-J02': 'roles',
        'ADM-J03': 'menus',
        'ADM-J04': 'scope',
        'ADM-J05': 'fields',
        'ADM-J06': 'sensitive',
        'ADM-J11': 'organizations',
        'ADM-A07': 'access',
      } as const
    )[pageId as 'ADM-J01'] ?? 'staff'
  );
}
export type PolicyEffect = 'ALLOW' | 'DENY';
export interface FunctionRule {
  permissionId: string;
  effect: PolicyEffect;
}
export interface BusinessScopeGroup {
  appId: string;
  cityIds: string[] | null;
  productIds?: string[] | null;
  orderIds?: string[] | null;
  includeCentralOrders?: boolean;
}
export interface PersonalAccess {
  permissions: FunctionRule[];
  businessScopeGroups: BusinessScopeGroup[];
  fieldPolicies?: FieldRule[];
}
export interface FieldRule {
  permissionId: string;
  fieldName: string;
  classification: string;
  access: 'ALLOW' | 'MASK' | 'DENY';
}
export function canonicalFieldRules(rules: readonly FieldRule[]): FieldRule[] {
  const byField = new Map<string, FieldRule>();
  const priority = { ALLOW: 0, MASK: 1, DENY: 2 };
  for (const rule of rules) {
    const fieldName = canonicalBusinessFieldName(rule.fieldName);
    const key = `${rule.permissionId}:${fieldName}`;
    const existing = byField.get(key);
    if (!existing || priority[rule.access] > priority[existing.access])
      byField.set(key, { ...rule, fieldName });
  }
  return [...byField.values()];
}
export function commonFieldsForGrantedFunctions(
  existing: readonly FieldRule[],
  previous: readonly FunctionRule[],
  next: readonly FunctionRule[],
  catalog: readonly SecurityPermission[],
  protectedRules: readonly FieldRule[] = [],
): FieldRule[] {
  const addedIds = new Set(
    next
      .filter(
        (rule) =>
          rule.effect === 'ALLOW' &&
          !previous.some((old) => old.permissionId === rule.permissionId && old.effect === 'ALLOW'),
      )
      .map((rule) => rule.permissionId),
  );
  if (!addedIds.size) return existing.map((rule) => ({ ...rule }));
  const preserved = canonicalFieldRules(existing);
  for (const rule of canonicalFieldRules(protectedRules)) {
    if (rule.access === 'ALLOW' || !addedIds.has(rule.permissionId)) continue;
    if (
      !preserved.some(
        (item) =>
          item.permissionId === rule.permissionId &&
          canonicalBusinessFieldName(item.fieldName) === canonicalBusinessFieldName(rule.fieldName),
      )
    )
      preserved.push({ ...rule });
  }
  const codes = catalog
    .filter((permission) => addedIds.has(permission.id))
    .map((permission) => permission.code);
  return ensureCommonReadFieldPolicies(preserved as BusinessReadFieldRule[], codes, catalog).rules;
}
export interface StateRule {
  permissionId: string;
  state: string;
  effect: PolicyEffect;
}
export interface SecurityRole {
  id: string;
  name: string;
  description: string;
  status: string;
  version: number;
  permissions: FunctionRule[];
  businessScopeGroups: BusinessScopeGroup[] | null;
}
export interface SecurityPermission {
  id: string;
  code: string;
  highRisk: boolean;
  status: string;
  description: string;
}
export interface SecurityOrganization {
  id: string;
  name: string;
  parentId: string | null;
  status: string;
  version: number;
}
export interface SecurityUser {
  id: string;
  username: string;
  displayName: string;
  organizationId: string | null;
  status: string;
  version: number;
  roleIds: string[];
  credentialReady: boolean;
  personalAccess?: PersonalAccess | null;
}
export interface NamedResource {
  id: string;
  name: string;
  cityIds: string[] | null;
  status: string;
}
export interface BusinessSecurityWorkspace {
  users: SecurityUser[];
  roles: SecurityRole[];
  permissions: SecurityPermission[];
  organizations: SecurityOrganization[];
  apps: NamedResource[];
  cities: NamedResource[];
  products: { id: string; name: string; cityId: string; appIds: string[]; status: string }[];
  orders: {
    id: string;
    orderNo: string;
    productId: string;
    cityId: string;
    appId: string;
    organizationId: string | null;
  }[];
  orderCandidatesLimit?: number;
  fieldPolicies: (FieldRule & { roleId: string; id: string })[];
  statePolicies: (StateRule & { roleId: string; id: string })[];
  centralOrders: JsonRecord[];
  currentUser: {
    id: string;
    organizationId: string | null;
    canManage: boolean;
    visibleOrganizationIds: string[];
  };
}
export const securityText = (row: JsonRecord, key: string, fallback = '') =>
  typeof row[key] === 'string' ? (row[key] as string) : fallback;
export const securityRecord = (value: unknown): JsonRecord => (isRecord(value) ? value : {});
const rows = (value: unknown): JsonRecord[] => (Array.isArray(value) ? value.filter(isRecord) : []);
const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
export const readScopeGroups = (value: unknown): BusinessScopeGroup[] =>
  rows(value).map((group) => ({
    appId: securityText(group, 'appId'),
    cityIds: group.cityIds === null ? null : strings(group.cityIds),
    ...(group.productIds === undefined
      ? {}
      : { productIds: group.productIds === null ? null : strings(group.productIds) }),
    ...(group.orderIds === undefined
      ? {}
      : { orderIds: group.orderIds === null ? null : strings(group.orderIds) }),
    ...(typeof group.includeCentralOrders === 'boolean'
      ? { includeCentralOrders: group.includeCentralOrders }
      : {}),
  }));
export function readBusinessWorkspace(raw: unknown): BusinessSecurityWorkspace {
  const value = securityRecord(raw);
  if (
    !Array.isArray(value.users) ||
    !Array.isArray(value.roles) ||
    !Array.isArray(value.permissions)
  )
    throw new Error('授权资料未完整读取，请重新加载后再配置。');
  const resources = (value: unknown, app: boolean): NamedResource[] =>
    rows(value).map((item) => {
      const bindings = Array.isArray(item.cities)
        ? rows(item.cities)
        : Array.isArray(item.cityConfigs)
          ? rows(item.cityConfigs)
          : Array.isArray(item.appCityConfigs)
            ? rows(item.appCityConfigs)
            : null;
      return {
        id: securityText(item, 'id'),
        name: securityText(item, app ? 'displayName' : 'name', app ? '未命名小程序' : '未命名城市'),
        status: securityText(item, 'status', 'ACTIVE'),
        cityIds: bindings
          ? bindings
              .filter((binding) => binding.enabled !== false)
              .map(
                (binding) =>
                  securityText(binding, 'cityId') ||
                  securityText(securityRecord(binding.city), 'id'),
              )
              .filter(Boolean)
          : Array.isArray(item.cityIds)
            ? strings(item.cityIds)
            : null,
      };
    });
  const currentUser = securityRecord(value.currentUser);
  return {
    users: rows(value.users).map((item) => ({
      id: securityText(item, 'id'),
      username: securityText(item, 'username'),
      displayName: securityText(item, 'displayName', '未命名员工'),
      organizationId: securityText(item, 'organizationId') || null,
      status: securityText(item, 'status', 'ACTIVE'),
      version: Number(item.version ?? 0),
      roleIds: rows(item.roles)
        .map((ref) => securityText(ref, 'roleId') || securityText(securityRecord(ref.role), 'id'))
        .filter(Boolean),
      credentialReady:
        item.credentialEnrollment === 'ENROLLED' ||
        Boolean(item.adminCredential && isRecord(item.adminCredential)),
      personalAccess: isRecord(item.personalAccess)
        ? {
            permissions: rows(item.personalAccess.permissions).map((rule) => ({
              permissionId: securityText(rule, 'permissionId'),
              effect: rule.effect === 'DENY' ? 'DENY' : 'ALLOW',
            })),
            businessScopeGroups: readScopeGroups(item.personalAccess.businessScopeGroups),
            ...(Array.isArray(item.personalAccess.fieldPolicies)
              ? {
                  fieldPolicies: rows(item.personalAccess.fieldPolicies).map((rule) => ({
                    permissionId: securityText(rule, 'permissionId'),
                    fieldName: securityText(rule, 'fieldName'),
                    classification: securityText(rule, 'classification'),
                    access:
                      rule.access === 'DENY'
                        ? ('DENY' as const)
                        : rule.access === 'MASK'
                          ? ('MASK' as const)
                          : ('ALLOW' as const),
                  })),
                }
              : {}),
          }
        : null,
    })),
    roles: rows(value.roles)
      .filter((item) => !item.personalForUserId)
      .map((item) => ({
        id: securityText(item, 'id'),
        name: securityText(item, 'name', '未命名岗位'),
        description: securityText(item, 'description'),
        status: securityText(item, 'status', 'ACTIVE'),
        version: Number(item.version ?? 0),
        permissions: rows(item.permissions)
          .map<FunctionRule>((ref) => ({
            permissionId:
              securityText(ref, 'permissionId') ||
              securityText(securityRecord(ref.permission), 'id'),
            effect: ref.effect === 'DENY' ? 'DENY' : 'ALLOW',
          }))
          .filter((rule) => rule.permissionId),
        businessScopeGroups: Array.isArray(item.businessScopeGroups)
          ? readScopeGroups(item.businessScopeGroups)
          : null,
      })),
    permissions: rows(value.permissions).map((item) => ({
      id: securityText(item, 'id'),
      code: securityText(item, 'code'),
      highRisk: item.highRisk === true,
      status: securityText(item, 'status', 'ACTIVE'),
      description: securityText(item, 'description'),
    })),
    organizations: rows(value.organizations).map((item) => ({
      id: securityText(item, 'id'),
      name: securityText(item, 'name', '未命名组织'),
      parentId: securityText(item, 'parentId') || null,
      status: securityText(item, 'status', 'ACTIVE'),
      version: Number(item.version ?? 0),
    })),
    apps: resources(value.apps, true),
    cities: resources(value.cities, false),
    products: rows(value.products).map((item) => ({
      id: securityText(item, 'id'),
      name: securityText(item, 'name', securityText(item, 'title', '未命名线路')),
      cityId: securityText(item, 'cityId'),
      appIds: strings(item.appIds),
      status: securityText(item, 'status', 'ACTIVE'),
    })),
    orders: rows(value.orders).map((item) => ({
      id: securityText(item, 'id'),
      orderNo: securityText(item, 'orderNo', '未命名订单'),
      productId: securityText(item, 'productId'),
      cityId: securityText(item, 'cityId', securityText(item, 'sourceCityId')),
      appId: securityText(item, 'appId', securityText(item, 'sourceAppId')),
      organizationId:
        securityText(item, 'organizationId', securityText(item, 'ownerOrganizationId')) || null,
    })),
    orderCandidatesLimit: Number(value.orderCandidatesLimit ?? 500),
    fieldPolicies: rows(value.fieldPolicies).map((item) => ({
      id: securityText(item, 'id'),
      roleId: securityText(item, 'roleId'),
      permissionId: securityText(item, 'permissionId'),
      fieldName: securityText(item, 'fieldName'),
      classification: securityText(item, 'classification'),
      access: item.access === 'ALLOW' ? 'ALLOW' : item.access === 'MASK' ? 'MASK' : 'DENY',
    })),
    statePolicies: rows(value.statePolicies).map((item) => ({
      id: securityText(item, 'id'),
      roleId: securityText(item, 'roleId'),
      permissionId: securityText(item, 'permissionId'),
      state: securityText(item, 'state'),
      effect: item.effect === 'DENY' ? 'DENY' : 'ALLOW',
    })),
    centralOrders: rows(value.centralOrders),
    currentUser: {
      id: securityText(currentUser, 'id'),
      organizationId: securityText(currentUser, 'organizationId') || null,
      canManage: currentUser.canManage === true,
      visibleOrganizationIds: strings(currentUser.visibleOrganizationIds),
    },
  };
}

export type PermissionAction = 'view' | 'maintain' | 'export' | 'sensitive';
export interface PermissionPresentation {
  groupId: string;
  groupName: string;
  title: string;
  action: PermissionAction;
}
const definitions: [string, string, string, PermissionAction][] = [
  ['city.view', 'cities', '查看城市', 'view'],
  ['city.manage', 'cities', '维护城市与景点', 'maintain'],
  ['app.view', 'apps', '查看小程序', 'view'],
  ['app.manage', 'apps', '维护小程序配置', 'maintain'],
  ['app.product.manage', 'apps', '维护小程序产品展示', 'maintain'],
  ['product.view', 'products', '查看线路与套餐', 'view'],
  ['product.manage', 'products', '维护线路、套餐和图片', 'maintain'],
  ['ad_landing.view', 'ad_landings', '查看广告落地页', 'view'],
  ['ad_landing.manage', 'ad_landings', '新建与编辑广告落地页', 'maintain'],
  ['ad_landing.publish', 'ad_landings', '发布与停用广告落地页', 'sensitive'],
  ['cms.view', 'cms', '查看页面装修', 'view'],
  ['cms.manage', 'cms', '编辑页面内容', 'maintain'],
  ['cms.publish', 'cms', '发布页面装修', 'sensitive'],
  ['order.view', 'orders', '查看订单', 'view'],
  ['order.confirm', 'orders', '确认订单', 'maintain'],
  ['order.fulfillment.manage', 'orders', '安排行程与履约', 'maintain'],
  ['order.complete', 'orders', '完成订单', 'maintain'],
  ['order.communication.view', 'orders', '查看订单沟通记录', 'view'],
  ['order.communication.create', 'orders', '记录订单沟通', 'maintain'],
  ['order.export', 'orders', '导出订单', 'export'],
  ['order.delete', 'orders', '删除可清理订单', 'sensitive'],
  ['refund.view', 'orders', '查看退款申请与流水', 'view'],
  ['refund.process', 'orders', '处理退款', 'sensitive'],
  ['after_sales.view', 'orders', '查看售后工单', 'view'],
  ['after_sales.manage', 'orders', '处理售后', 'maintain'],
  ['after_sales.compensation.record', 'orders', '登记售后补偿', 'sensitive'],
  ['member.view', 'members', '查看会员', 'view'],
  ['member.events.view', 'members', '查看会员浏览记录', 'view'],
  ['member.tags.view', 'members', '查看会员标签', 'view'],
  ['member.tags.manage', 'members', '维护会员标签', 'maintain'],
  ['member.note.manage', 'members', '维护会员备注', 'maintain'],
  ['member.analytics.view', 'members', '查看会员分析', 'view'],
  ['member.export', 'members', '导出会员资料', 'export'],
  ['marketing.coupon.view', 'marketing', '查看优惠券活动', 'view'],
  ['marketing.coupon.manage', 'marketing', '维护优惠券活动', 'maintain'],
  ['marketing.rescue.view', 'marketing', '查看支付挽留', 'view'],
  ['marketing.rescue.manage', 'marketing', '维护支付挽留', 'maintain'],
  ['channel.view', 'marketing', '查看渠道与组织', 'view'],
  ['channel.manage', 'marketing', '维护渠道与组织', 'maintain'],
  ['channel.promoter.view', 'marketing', '查看推广员', 'view'],
  ['channel.promoter.manage', 'marketing', '维护推广员', 'maintain'],
  ['channel.link.view', 'marketing', '查看推广入口', 'view'],
  ['channel.link.manage', 'marketing', '维护推广入口', 'maintain'],
  ['channel.performance.view', 'marketing', '查看渠道业绩', 'view'],
  ['commission.rule.view', 'marketing', '查看佣金规则', 'view'],
  ['commission.rule.manage', 'marketing', '维护佣金规则', 'sensitive'],
  ['commission.view', 'finance', '查看佣金账目', 'view'],
  ['commission.export', 'finance', '导出佣金账目', 'export'],
  ['settlement.view', 'finance', '查看结算', 'view'],
  ['settlement.generate', 'finance', '生成结算批次', 'sensitive'],
  ['settlement.review', 'finance', '复核结算', 'sensitive'],
  ['settlement.execute', 'finance', '执行结算', 'sensitive'],
  ['payment.view', 'payments', '查看支付流水', 'view'],
  ['merchant.change', 'payments', '变更收款商户与路由', 'sensitive'],
  ['message.view', 'messages', '查看消息与通知', 'view'],
  ['message.manage', 'messages', '维护通知模板与发送', 'maintain'],
  ['finance.view', 'finance', '查看财务账目', 'view'],
  ['finance.reconcile', 'finance', '执行支付对账', 'sensitive'],
  ['finance.export', 'finance', '导出财务账目', 'export'],
  ['finance.rule.view', 'finance', '查看成本规则', 'view'],
  ['finance.rule.manage', 'finance', '维护成本规则', 'maintain'],
  ['report.view', 'finance', '查看经营报表', 'view'],
  ['report.export', 'finance', '导出经营报表', 'export'],
  ['release.view', 'system', '查看小程序发布版本', 'view'],
  ['release.manage', 'system', '上传与发布小程序版本', 'sensitive'],
  ['ops.monitor.view', 'system', '查看系统运行与告警', 'view'],
  ['ops.monitor.manage', 'system', '维护系统告警与运维', 'sensitive'],
  ['ops.recovery.view', 'system', '查看备份与恢复记录', 'view'],
  ['ops.recovery.manage', 'system', '执行备份恢复', 'sensitive'],
  ['system.security.view', 'security', '查看员工与授权', 'view'],
  ['system.security.manage', 'security', '管理组织、员工及授权', 'sensitive'],
];
export const BUSINESS_PERMISSION_GROUPS = [
  { id: 'apps', name: '多小程序管理' },
  { id: 'cities', name: '城市与地区管理' },
  { id: 'products', name: '线路产品管理' },
  { id: 'ad_landings', name: '广告页管理' },
  { id: 'cms', name: '可视化装修' },
  { id: 'orders', name: '订单履约' },
  { id: 'members', name: '会员与用户' },
  { id: 'marketing', name: '营销与分销' },
  { id: 'payments', name: '支付中心' },
  { id: 'messages', name: '消息与通知' },
  { id: 'finance', name: '财务报表' },
  { id: 'system', name: '系统设置与发布' },
  { id: 'security', name: '权限与角色' },
  { id: 'other', name: '额外业务权限' },
];
const menuModules = [
  ['经营总览', '/admin/dashboard'],
  ['多小程序管理', '/admin/apps'],
  ['城市与地区管理', '/admin/cities'],
  ['线路产品管理', '/admin/products'],
  ['广告页管理', '/admin/ad-landings'],
  ['可视化装修（CMS）', '/admin/cms/templates'],
  ['订单履约', '/admin/orders'],
  ['会员与用户', '/admin/members'],
  ['营销与分销', '/admin/marketing/coupons'],
  ['支付中心', '/admin/payments/merchants'],
  ['消息与通知', '/admin/messages/templates'],
  ['财务报表', '/admin/finance/order-profit'],
  ['系统设置', '/admin/system/settings'],
  ['权限与角色', '/admin/security/users'],
  ['操作日志', '/admin/audit/overview'],
] as const;
export function businessMenuPreview(
  rules: readonly FunctionRule[],
  permissions: readonly SecurityPermission[],
) {
  const granted = new Set(
    permissions
      .filter(
        (permission) =>
          permission.status === 'ACTIVE' &&
          rules.some((rule) => rule.permissionId === permission.id && rule.effect === 'ALLOW'),
      )
      .map((permission) => permission.code),
  );
  return menuModules.map(([name, path]) => ({
    name,
    entries: (path === '/admin/security/users' ? moduleEntries(path) : adminPages)
      .filter(
        (page) =>
          page.id !== 'ADM-A01' && !page.path.includes(':') && modulePathForPage(page.id) === path,
      )
      .map((page) => ({
        title: page.title,
        visible:
          page.id === 'ADM-A07' ||
          (adminMenuPermissions[page.id] ?? []).some((code) => granted.has(code)),
      })),
  }));
}
const presentations = new Map(
  definitions.map(([code, groupId, title, action]) => [
    code,
    {
      groupId,
      groupName: BUSINESS_PERMISSION_GROUPS.find((group) => group.id === groupId)!.name,
      title,
      action,
    },
  ]),
);
export function presentPermission(
  permission: SecurityPermission,
  index = 0,
): PermissionPresentation {
  return (
    presentations.get(permission.code) ?? {
      groupId: 'other',
      groupName: '额外业务权限',
      title: /[\u3400-\u9fff]/u.test(permission.description)
        ? permission.description
        : `其他业务操作 ${index + 1}`,
      action: permission.highRisk ? 'sensitive' : 'maintain',
    }
  );
}
export const permissionActionName = (action: PermissionAction) =>
  ({ view: '查看', maintain: '维护', export: '导出', sensitive: '重要操作' })[action];
export function updateFunctionRule(
  rules: readonly FunctionRule[],
  permissionId: string,
  effect: PolicyEffect | null,
): FunctionRule[] {
  return [
    ...rules.filter((rule) => rule.permissionId !== permissionId).map((rule) => ({ ...rule })),
    ...(effect ? [{ permissionId, effect }] : []),
  ];
}
/** Action grants need their real workbench read permission; never silently override an explicit deny. */
const actionViewDependencies = businessViewDependencies;
export function updateBusinessPermission(
  rules: readonly FunctionRule[],
  permissions: readonly SecurityPermission[],
  permissionId: string,
  selected: boolean,
): FunctionRule[] {
  const permission = permissions.find((item) => item.id === permissionId);
  if (!permission) return rules.map((rule) => ({ ...rule }));
  let next = updateFunctionRule(rules, permissionId, selected ? 'ALLOW' : null);
  if (selected) {
    let requiredCode = actionViewDependencies[permission.code];
    const visited = new Set<string>([permission.code]);
    while (requiredCode && !visited.has(requiredCode)) {
      visited.add(requiredCode);
      const required = permissions.find(
        (item) => item.code === requiredCode && item.status === 'ACTIVE',
      );
      if (!required) throw new Error('此操作所需的查看权限尚未配置，暂不能授权。');
      if (rules.some((rule) => rule.permissionId === required.id && rule.effect === 'DENY'))
        throw new Error(
          `“${presentPermission(required).title}”已明确禁止，请先单独确认解除禁止，再授权此操作。`,
        );
      next = updateFunctionRule(next, required.id, 'ALLOW');
      requiredCode = actionViewDependencies[required.code];
    }
  } else {
    const dependent = permissions.find(
      (item) =>
        actionViewDependencies[item.code] === permission.code &&
        next.some((rule) => rule.permissionId === item.id && rule.effect === 'ALLOW'),
    );
    if (dependent)
      throw new Error(`请先取消“${presentPermission(dependent).title}”，再取消它所需的查看权限。`);
  }
  return next;
}
export function updateBusinessGroup(
  rules: readonly FunctionRule[],
  allPermissions: readonly SecurityPermission[],
  groupPermissions: readonly SecurityPermission[],
  selected: boolean,
): FunctionRule[] {
  let next = rules.map((rule) => ({ ...rule }));
  const ordinary = groupPermissions.filter(
    (permission) =>
      permission.status === 'ACTIVE' &&
      !permission.highRisk &&
      presentPermission(permission).action !== 'sensitive' &&
      !rules.some((rule) => rule.permissionId === permission.id && rule.effect === 'DENY'),
  );
  const dependencyDepth = (code: string): number => {
    let depth = 0;
    const visited = new Set<string>();
    while (actionViewDependencies[code] && !visited.has(code)) {
      visited.add(code);
      code = actionViewDependencies[code]!;
      depth++;
    }
    return depth;
  };
  const ordered = selected
    ? ordinary
    : [...ordinary].sort((a, b) => dependencyDepth(b.code) - dependencyDepth(a.code));
  for (const permission of ordered)
    next = updateBusinessPermission(next, allPermissions, permission.id, selected);
  return next;
}
/** Parent selection touches ordinary visible actions only; sensitive grants remain an explicit choice. */
export function updateOrdinaryGroup(
  rules: readonly FunctionRule[],
  permissions: readonly SecurityPermission[],
  selected: boolean,
): FunctionRule[] {
  let result = rules.map((rule) => ({ ...rule }));
  for (const permission of permissions.filter(
    (item) =>
      item.status === 'ACTIVE' && !item.highRisk && presentPermission(item).action !== 'sensitive',
  ))
    if (!result.some((rule) => rule.permissionId === permission.id && rule.effect === 'DENY'))
      result = updateFunctionRule(result, permission.id, selected ? 'ALLOW' : null);
  return result;
}
export const cloneRole = (role: SecurityRole): SecurityRole => ({
  ...role,
  permissions: role.permissions.map((rule) => ({ ...rule })),
  businessScopeGroups:
    role.businessScopeGroups?.map((group) => ({
      ...group,
      cityIds: group.cityIds?.slice() ?? null,
      ...(group.productIds === undefined ? {} : { productIds: group.productIds?.slice() ?? null }),
      ...(group.orderIds === undefined ? {} : { orderIds: group.orderIds?.slice() ?? null }),
    })) ?? null,
});
export function roleDirty(
  saved: SecurityRole | undefined,
  draft: SecurityRole | undefined,
): boolean {
  return Boolean(saved && draft && JSON.stringify(saved) !== JSON.stringify(draft));
}
export function updateScopeCity(
  groups: readonly BusinessScopeGroup[],
  appId: string,
  cityId: string,
  selected: boolean,
): BusinessScopeGroup[] {
  const previous = groups.find((group) => group.appId === appId);
  const cityIds = new Set(previous?.cityIds ?? []);
  if (selected) cityIds.add(cityId);
  else cityIds.delete(cityId);
  return [
    ...groups
      .filter((group) => group.appId !== appId)
      .map((group) => ({ ...group, cityIds: group.cityIds?.slice() ?? null })),
    ...(cityIds.size ? [{ ...previous, appId, cityIds: [...cityIds] }] : []),
  ];
}
export interface OrganizationTreeEntry {
  organization: SecurityOrganization;
  depth: number;
  cycle: boolean;
  orphan: boolean;
}
export function organizationTree(
  organizations: readonly SecurityOrganization[],
): OrganizationTreeEntry[] {
  const byId = new Map(organizations.map((item) => [item.id, item]));
  const visited = new Set<string>();
  const result: OrganizationTreeEntry[] = [];
  function visit(item: SecurityOrganization, depth: number, ancestors: ReadonlySet<string>) {
    if (visited.has(item.id)) return;
    visited.add(item.id);
    const cycle = ancestors.has(item.id);
    const orphan = Boolean(item.parentId && !byId.has(item.parentId));
    result.push({ organization: item, depth, cycle, orphan });
    const next = new Set([...ancestors, item.id]);
    organizations
      .filter((child) => child.parentId === item.id)
      .forEach((child) => visit(child, depth + 1, next));
  }
  organizations
    .filter((item) => !item.parentId || !byId.has(item.parentId))
    .forEach((item) => visit(item, 0, new Set()));
  organizations
    .filter((item) => !visited.has(item.id))
    .forEach((item) => visit(item, 0, new Set([item.id])));
  return result;
}
/** An enabled leaf is usable only if every ancestor exists, is enabled and the chain has no cycle. */
export function organizationEffectiveActive(
  organizations: readonly Pick<SecurityOrganization, 'id' | 'parentId' | 'status'>[],
  organizationId: string,
): boolean {
  const byId = new Map(organizations.map((item) => [item.id, item]));
  const visited = new Set<string>();
  let id: string | null = organizationId;
  while (id !== null) {
    if (visited.has(id)) return false;
    visited.add(id);
    const item = byId.get(id);
    if (!item || item.status !== 'ACTIVE') return false;
    id = item.parentId;
  }
  return true;
}
export function organizationParentOptions(
  organizations: readonly SecurityOrganization[],
  id: string,
): SecurityOrganization[] {
  const excluded = new Set([id]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const item of organizations)
      if (item.parentId && excluded.has(item.parentId) && !excluded.has(item.id)) {
        excluded.add(item.id);
        changed = true;
      }
  }
  return organizations.filter(
    (item) => organizationEffectiveActive(organizations, item.id) && !excluded.has(item.id),
  );
}
export const FIELD_CHOICES: { name: string; label: string; classification: string }[] = [
  { name: 'mobile', label: '联系电话', classification: 'L1' },
  { name: 'contact.mobile', label: '订单联系人电话', classification: 'L1' },
  { name: 'contactName', label: '联系人姓名', classification: 'L1' },
  { name: 'contactWechat', label: '联系人微信', classification: 'L1' },
  { name: 'wechatNo', label: '订单联系人微信', classification: 'L1' },
  { name: 'grossAmountCents', label: '订单总金额', classification: 'L2' },
  { name: 'discountAmountCents', label: '订单优惠金额', classification: 'L2' },
  { name: 'payAmountCents', label: '订单应付金额', classification: 'L2' },
  { name: 'actualPaidAmountCents', label: '订单实际支付金额', classification: 'L2' },
  { name: 'amountCents', label: '退款或支付流水金额', classification: 'L2' },
  { name: 'currency', label: '金额币种', classification: 'L2' },
  { name: 'outTradeNo', label: '支付交易单号', classification: 'L2' },
  { name: 'paymentMchid', label: '交易收款商户号', classification: 'L2' },
  { name: 'originalPaymentMchid', label: '原支付收款商户号', classification: 'L2' },
  { name: 'costAmountCents', label: '订单成本', classification: 'L2' },
  { name: 'paidAmountCents', label: '实际收款金额', classification: 'L2' },
  { name: 'vehicleCostCents', label: '车辆成本', classification: 'L2' },
  { name: 'ticketCostCents', label: '门票成本', classification: 'L2' },
  { name: 'guideCostCents', label: '导游成本', classification: 'L2' },
  { name: 'otherCostCents', label: '其他成本', classification: 'L2' },
  { name: 'paymentFeeBps', label: '支付手续费率', classification: 'L2' },
  { name: 'commissionAmountCents', label: '佣金金额', classification: 'L2' },
  { name: 'grossProfitCents', label: '订单利润', classification: 'L2' },
  { name: 'refundAmountCents', label: '退款金额', classification: 'L2' },
  { name: 'paymentSecret', label: '支付密钥与证书', classification: 'L3' },
];
export function replaceFieldRule(rules: readonly FieldRule[], replacement: FieldRule): FieldRule[] {
  if (replacement.classification === 'L3' && replacement.access === 'ALLOW')
    throw new Error('支付密钥和证书不能设置为明文可见。');
  return [
    ...rules
      .filter(
        (rule) =>
          !(
            rule.permissionId === replacement.permissionId &&
            rule.fieldName === replacement.fieldName
          ),
      )
      .map((rule) => ({ ...rule })),
    { ...replacement },
  ];
}
export const BUSINESS_STATES: { value: string; name: string }[] = [
  { value: 'PENDING_PAYMENT', name: '待支付' },
  { value: 'PAID_NEED_TRIP_INFO', name: '已支付待补资料' },
  { value: 'PAID_INFO_READY', name: '待运营确认' },
  { value: 'PENDING_CONFIRM', name: '待确认' },
  { value: 'CONFIRMED', name: '已确认' },
  { value: 'PENDING_FULFILLMENT', name: '待履约' },
  { value: 'FULFILLMENT_READY', name: '行程准备完成' },
  { value: 'IN_SERVICE', name: '服务进行中' },
  { value: 'COMPLETED', name: '已完成' },
  { value: 'PENDING_REVIEW', name: '待复核' },
  { value: 'APPROVED', name: '已批准' },
  { value: 'REFUND_REQUESTED', name: '申请退款' },
  { value: 'PROCESSING', name: '处理中' },
];
export function replaceStateRule(rules: readonly StateRule[], replacement: StateRule): StateRule[] {
  return [
    ...rules
      .filter(
        (rule) =>
          !(rule.permissionId === replacement.permissionId && rule.state === replacement.state),
      )
      .map((rule) => ({ ...rule })),
    { ...replacement },
  ];
}
